import React from "react";
import { LegalLayout, Section } from "./legal/LegalLayout";

export default function Terms() {
  return (
    <LegalLayout
      testId="terms-page"
      eyebrow="Legal"
      title="Terms of"
      accent="Service."
      lastUpdated="July 5, 2026"
    >
      <p className="text-base text-slate-600 leading-relaxed mb-2">
        These Terms of Service (&ldquo;<strong>Terms</strong>&rdquo;) govern your access to and use
        of Nexso &mdash; the remote digital signage platform operated at{" "}
        <a href="https://nexso.net" className="text-sky-600 hover:underline">nexso.net</a>{" "}
        (&ldquo;<strong>Nexso</strong>&rdquo;, &ldquo;<strong>we</strong>&rdquo;, &ldquo;<strong>us</strong>&rdquo;). By creating an account, pairing a screen, or
        otherwise using the service you agree to these Terms. If you do not agree,
        do not use Nexso.
      </p>

      <Section id="service" title="1. What Nexso provides">
        <p>
          Nexso is a Software-as-a-Service (SaaS) platform that lets business
          owners design digital menus, ads, and signage from a web dashboard and
          publish that content in real time to one or more Android-powered screens
          (TVs, sticks, kiosks) paired to their account.
        </p>
        <p>The service currently includes:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Web dashboard for designing, scheduling, and managing signage content</li>
          <li>Android launcher application (&ldquo;Nexso Launcher&rdquo;) that runs on customer-owned hardware</li>
          <li>Template library, media library, playlist manager, apps &amp; widgets</li>
          <li>Optional AI-assisted image and text generation</li>
          <li>Optional third-party integrations (Canva, Stripe, Telegram, etc.)</li>
        </ul>
      </Section>

      <Section id="accounts" title="2. Accounts &amp; eligibility">
        <p>
          You must be at least 18 years old, or the age of legal majority in your
          jurisdiction, to create an account. You represent that all information
          you provide is accurate and that you have authority to bind your business.
        </p>
        <p>
          You are responsible for keeping your password secret and for all activity
          that happens under your account. Notify us immediately at{" "}
          <a href="mailto:security@nexso.net" className="text-sky-600 hover:underline">security@nexso.net</a>{" "}
          if you suspect unauthorized access.
        </p>
      </Section>

      <Section id="plans" title="3. Plans, trials &amp; billing">
        <p>
          New accounts start on a <strong>14-day free Pro trial</strong> with no card
          required. At the end of the trial you may either downgrade to the Free
          plan (limited to 1 screen) or subscribe to a paid plan.
        </p>
        <p>
          Paid plans are billed <strong>monthly in advance</strong> via Stripe. The
          base subscription is $9.99/month and includes one screen. Each additional
          paired screen adds $4.99/month, prorated automatically when you add or
          remove screens mid-cycle.
        </p>
        <p>
          <strong>AI Credits</strong> are sold in one-time packs (currently $10 / $25 / $50) and are
          consumed by premium generations. Credits are non-refundable once used but
          otherwise do not expire.
        </p>
        <p>
          You can cancel at any time from the Billing page or the Stripe Customer
          Portal. Cancellation takes effect at the end of the current billing period
          &mdash; you retain access until then, but we do not refund partial months
          except where required by law.
        </p>
      </Section>

      <Section id="content" title="4. Your content">
        <p>
          You retain all rights to the designs, images, videos, text, brand assets,
          and other content you upload or create on Nexso (&ldquo;<strong>Your Content</strong>&rdquo;).
          By using the service you grant us a limited, worldwide, royalty-free
          license to host, store, process, and display Your Content solely to
          operate and improve the service on your behalf.
        </p>
        <p>You represent that you own or have all necessary rights to Your Content and that Your Content does not:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Infringe any copyright, trademark, or other intellectual-property right</li>
          <li>Contain unlawful, defamatory, harassing, or misleading material</li>
          <li>Contain malware, spyware, or attempt to interfere with the service</li>
          <li>Depict minors in a sexually suggestive way, or otherwise violate applicable law</li>
        </ul>
        <p>We reserve the right to remove Your Content that we believe violates these Terms.</p>
      </Section>

      <Section id="ai" title="5. AI-assisted generation">
        <p>
          Nexso offers optional AI features that generate images and text from
          prompts you provide, using third-party models (OpenAI GPT Image 1,
          Gemini &ldquo;Nano Banana&rdquo;, and others). By using these features you acknowledge:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Outputs are generated by machine and may be inaccurate, biased, or unsuitable for a given use case</li>
          <li>You are solely responsible for reviewing outputs before publishing them to your screens</li>
          <li>Prompts and reference images you upload are sent to the underlying model provider for the purpose of generation</li>
          <li>Ownership of outputs is governed by the terms of the underlying model provider, in most cases granting you a broad usage license &mdash; but Nexso makes no warranty as to copyright status</li>
        </ul>
      </Section>

      <Section id="hardware" title="6. Screens &amp; hardware">
        <p>
          Nexso is device-agnostic. You may pair any Android TV / Android stick /
          Smart TV that can run our launcher application. We provide the launcher
          APK as-is and do not sell hardware directly (unless otherwise stated in
          writing).
        </p>
        <p>
          You are responsible for the electricity, network connection, physical
          installation, and legal display of screens on your premises, including any
          licenses required to play music, video, or copyrighted media.
        </p>
      </Section>

      <Section id="acceptable-use" title="7. Acceptable use">
        <p>You agree not to, and not to allow any third party to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Use Nexso to display illegal, hateful, sexually explicit, or violence-inciting content</li>
          <li>Publish gambling, pyramid, or fraud content in jurisdictions where prohibited</li>
          <li>Attempt to reverse engineer, scrape, or overload our APIs beyond documented rate limits</li>
          <li>Circumvent screen or credit limits imposed by your plan</li>
          <li>Use Nexso to send unsolicited advertising, phishing, or malware to end users</li>
          <li>Resell, sublicense, or white-label Nexso without a separate written agreement</li>
        </ul>
        <p>Violation may result in immediate suspension or termination of your account without refund.</p>
      </Section>

      <Section id="third-party" title="8. Third-party services">
        <p>
          Nexso integrates with several third-party services (Stripe for payments,
          Canva for design import, OpenAI &amp; Google for AI, Telegram for support,
          Pexels &amp; Unsplash for stock imagery, etc.). Your use of those integrations
          is subject to their own terms and privacy policies. We are not responsible
          for outages, pricing changes, or policy changes on their side.
        </p>
      </Section>

      <Section id="ip" title="9. Intellectual property">
        <p>
          The Nexso brand, wordmark, sky-blue &ldquo;N&rdquo; logo, dashboard UI,
          Android launcher code, template library, backend, and documentation are
          the exclusive property of Nexso and protected by copyright, trademark, and
          other laws. You receive a limited, non-exclusive, non-transferable license
          to use the service for the duration of your subscription. No other rights
          are granted by implication or otherwise.
        </p>
      </Section>

      <Section id="warranty" title="10. Warranty disclaimer">
        <p>
          The service is provided <strong>&ldquo;AS IS&rdquo; and &ldquo;AS AVAILABLE&rdquo;</strong> without
          warranties of any kind, whether express, implied, or statutory, including
          any implied warranties of merchantability, fitness for a particular
          purpose, title, and non-infringement. We do not warrant uninterrupted or
          error-free operation. We recommend backing up designs to your own
          machine periodically.
        </p>
      </Section>

      <Section id="liability" title="11. Limitation of liability">
        <p>
          To the maximum extent permitted by law, Nexso&rsquo;s total aggregate
          liability arising out of or related to the service will not exceed the
          greater of <strong>USD $100</strong> or the amounts you paid us in the twelve (12)
          months immediately preceding the claim.
        </p>
        <p>
          In no event will we be liable for any indirect, incidental, special,
          consequential, exemplary, or punitive damages, or for lost profits,
          revenue, goodwill, or data, even if advised of the possibility.
        </p>
      </Section>

      <Section id="indemnity" title="12. Indemnity">
        <p>
          You agree to indemnify and hold Nexso, its officers, employees, and
          contractors harmless from any claim, damages, liability, cost, or expense
          (including reasonable attorneys&rsquo; fees) arising out of Your Content,
          your use of the service, or your violation of these Terms.
        </p>
      </Section>

      <Section id="termination" title="13. Termination">
        <p>
          You may terminate your account at any time by deleting it from the Billing
          page or by emailing{" "}
          <a href="mailto:hello@nexso.net" className="text-sky-600 hover:underline">hello@nexso.net</a>.
          We may suspend or terminate your access if you materially breach these
          Terms or if we discontinue the service. On termination your right to use
          Nexso ends and we may delete Your Content after a reasonable grace period.
        </p>
      </Section>

      <Section id="changes" title="14. Changes to these Terms">
        <p>
          We may update these Terms from time to time. If we make material changes
          we&rsquo;ll notify you by email or via an in-app banner at least 14 days before
          they take effect. Continued use of Nexso after the effective date
          constitutes acceptance of the revised Terms.
        </p>
      </Section>

      <Section id="law" title="15. Governing law">
        <p>
          These Terms are governed by the laws of the jurisdiction in which Nexso is
          incorporated, without regard to conflict-of-law principles. Any dispute
          arising out of or related to these Terms will be resolved exclusively in
          the courts of that jurisdiction, unless local consumer-protection law
          grants you a mandatory alternative venue.
        </p>
      </Section>

      <Section id="contact" title="16. Contact">
        <p>
          Questions, notices, or complaints about these Terms should be sent to{" "}
          <a href="mailto:hello@nexso.net" className="text-sky-600 hover:underline">hello@nexso.net</a>.
          We&rsquo;ll do our best to respond within 5 business days.
        </p>
      </Section>
    </LegalLayout>
  );
}
